import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { NavSidebar } from "../NavSidebar/NavSidebar";
import { TooltipBubble } from "../HoverTip/HoverTip";
import { useTheme } from "../../theme/useTheme";
import { useAuthContext } from "../../auth/AuthContext";
import { useLogout } from "../../api/hooks/useAuth";
import { useOverview } from "../../api/hooks/useOverview";
import { useLogs } from "../../api/hooks/useLogs";
import { computeWindow } from "../../utils/period";
import { formatClockMsk, formatMoney, formatPct } from "../../utils/format";

const TITLES: Record<string, string> = {
  "/": "Обзор",
  "/trades": "Сделки",
  "/logs": "Логи",
  "/control": "Управление",
  "/users": "Пользователи",
};

function pageTitle(pathname: string): string {
  return TITLES[pathname] ?? "Обзор";
}

// Compact label-over-value stat for the header — Цена BTC/Баланс moved
// here from Overview's own KPI row (user request: keep them visible on
// every page, not just Обзор) at a deliberately smaller scale than
// KpiCard's own big card treatment (34px value there vs 16px here) — a
// header strip has nowhere near a KPI card's vertical room.
function HeaderStat({
  label,
  value,
  valueColor,
  suffix,
  tooltip,
}: {
  label: string;
  value: string;
  valueColor?: string;
  suffix?: ReactNode;
  tooltip?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  return (
    <div
      onMouseEnter={() => tooltip && setShow(true)}
      onMouseLeave={() => setShow(false)}
      style={{ position: "relative", display: "flex", flexDirection: "column", gap: 1, cursor: tooltip ? "help" : undefined }}
    >
      <span style={{ font: "500 9.5px 'Golos Text',sans-serif", color: "var(--fg3)", textTransform: "uppercase", letterSpacing: ".04em" }}>
        {label}
      </span>
      <span style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
        <span style={{ font: "700 16px 'JetBrains Mono',monospace", whiteSpace: "nowrap", color: valueColor ?? "var(--fg)" }}>{value}</span>
        {suffix}
      </span>
      {/* placement="bottom" — this stat sits at the very top of the
          viewport (AppShell's own sticky header), so TooltipBubble's
          default upward-opening placement had nowhere to open and was
          simply invisible (reported live). */}
      {tooltip && show && <TooltipBubble content={tooltip} placement="bottom" />}
    </div>
  );
}

// Matches the prototype's <header>: page title, BTC/USDC subtitle, an
// open-position state badge (LONG/SHORT, when a position is open), the
// Цена BTC/Баланс stats (see HeaderStat above), a live pulse dot + clock,
// theme toggle (shown only on the narrow/mobile layout, where the
// sidebar's own toggle is hidden by CSS).
export function AppShell() {
  const { pathname } = useLocation();
  const [theme, toggleTheme] = useTheme();
  const { user } = useAuthContext();
  const logoutMutation = useLogout();
  // AppShell only needs SOME recent window for its own purposes (open-
  // position badge, bot_status for the sidebar, and now Цена BTC/Баланс —
  // none of which are window-scoped server-side, see overview.py's own
  // comment) — not a user-navigable one like Overview's own period tabs.
  // Computed once at mount (empty deps), not every render, or a fresh
  // Date.now() each render would mint a new queryKey — and therefore a
  // refetch — practically every render.
  const window_ = useMemo(() => computeWindow("d7", 0, Date.now()), []);
  const { data: overview } = useOverview(window_.from.toISOString(), window_.to.toISOString());
  const { data: logs } = useLogs({ page_size: 1 });
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Header badge across ALL open positions, not just one — an earlier
  // version read a single overview.open_position (whichever ONE trade
  // happened to be picked server-side), silently showing just one bot's
  // direction even when several were open with different bot instances.
  const positions = overview?.open_positions ?? [];
  const longCount = positions.filter((p) => p.direction === "LONG").length;
  const shortCount = positions.length - longCount;
  // Single direction (the common case, including exactly one position) gets
  // the original plain "LONG"/"SHORT" pill; a genuine mix across bots gets
  // both counts rather than picking one arbitrarily.
  const badgeLabel = positions.length === 0 ? null : shortCount === 0 ? "LONG" : longCount === 0 ? "SHORT" : `${longCount}L · ${shortCount}S`;
  const stateColor = shortCount === 0 ? "var(--long)" : longCount === 0 ? "var(--short)" : "var(--fg2)";
  const stateSoft = shortCount === 0 ? "var(--longsoft)" : longCount === 0 ? "var(--shortsoft)" : "var(--surface2)";
  const clock = formatClockMsk(now);

  const kpi = overview?.kpi;
  const chgVal = kpi ? parseFloat(kpi.price_change_24h_pct) : NaN;
  // Same per-bot balance+status breakdown Overview's own KPI card used to
  // build inline — moved here verbatim (see this component's own Баланс
  // HeaderStat below) now that the widget itself lives in the header, not
  // Overview.tsx. Only built with more than one bot to show — a single-bot
  // deployment has nothing to break down.
  const botStatusById = new Map((overview?.bot_status.by_bot ?? []).map((b) => [b.bot_instance_id, b]));
  const balancesByBot = kpi?.balances_by_bot ?? [];
  const balanceBreakdown =
    balancesByBot.length > 1 ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {balancesByBot.map((b) => {
          const status = botStatusById.get(b.bot_instance_id);
          const statusLabel = !status || !status.fresh ? "неактивен" : status.trading_disabled ? "торговля выкл" : "активен";
          const statusColor = !status || !status.fresh ? "var(--short)" : status.trading_disabled ? "var(--warn)" : "var(--long)";
          return (
            <div key={b.bot_instance_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor, flex: "none" }} />
                <span style={{ color: "var(--fg2)" }}>{b.bot_label ?? b.display_name}</span>
                <span style={{ color: statusColor, font: "500 10px 'Golos Text',sans-serif" }}>{statusLabel}</span>
              </span>
              <span>{formatMoney(b.balance)}</span>
            </div>
          );
        })}
      </div>
    ) : undefined;

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: "var(--bg)", color: "var(--fg)" }} className="app-shell">
      <NavSidebar
        errCount={logs?.counts.error ?? 0}
        botStatus={overview?.bot_status}
        theme={theme}
        onToggleTheme={toggleTheme}
        isAdmin={!!user && user.role === "admin"}
        userDisplayName={user?.display_name}
        onLogout={() => logoutMutation.mutate()}
      />

      <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 24,
            flexWrap: "wrap",
            padding: "16px 28px",
            borderBottom: "1px solid var(--line)",
            background: "var(--surface)",
            position: "sticky",
            top: 0,
            zIndex: 30,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ font: "700 21px 'Golos Text',sans-serif", letterSpacing: "-.022em" }}>{pageTitle(pathname)}</span>
            <span style={{ font: "400 12.5px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>BTC/USDC · Hyperliquid</span>
          </div>
          {badgeLabel && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: "3px 10px 3px 7px",
                borderRadius: 20,
                background: stateSoft,
                border: "1px solid var(--line)",
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: stateColor }} />
              <span style={{ font: "700 11.5px 'Golos Text',sans-serif", letterSpacing: ".06em", color: stateColor }}>
                {badgeLabel}
              </span>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 12 }} />
          {kpi && (
            <HeaderStat
              label="Цена BTC"
              value={formatMoney(kpi.price)}
              suffix={
                !Number.isNaN(chgVal) ? (
                  <span style={{ font: "600 10.5px 'JetBrains Mono',monospace", color: chgVal >= 0 ? "var(--long)" : "var(--short)" }}>
                    {formatPct(kpi.price_change_24h_pct)}
                  </span>
                ) : undefined
              }
            />
          )}
          {kpi && <HeaderStat label="Баланс" value={formatMoney(kpi.balance)} tooltip={balanceBreakdown} />}
          <div style={{ display: "flex", alignItems: "center", gap: 6, font: "400 11.5px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--long)", animation: "livedot 2.6s ease-in-out infinite" }} />
            {clock} МСК
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="header-theme-btn"
            style={{
              display: "none",
              padding: "6px 11px",
              border: "1px solid var(--line)",
              borderRadius: 8,
              background: "transparent",
              color: "var(--fg2)",
              font: "600 11.5px 'JetBrains Mono',monospace",
              cursor: "pointer",
            }}
          >
            {theme === "dark" ? "DARK" : "LIGHT"}
          </button>
        </header>

        <Outlet />
      </main>
    </div>
  );
}
